import i18next, { InitOptions, ResourceKey } from 'i18next';
import { proxy, useSnapshot } from 'valtio';

import { configService, Locale, localeService } from '#/client';
import { MenuConfig } from '#/service/config';
import { prepareMenu } from '#/service/config/transform';
import { StorageKeys } from '#/service/constants';
import { applyTranslationBinding, ignoreError } from '#/service/utils';

import {
  detectLocale,
  fetchLocale,
  fetchLocales,
  getClientLocale,
} from './utils';

interface Translations {
  translations: ResourceKey;
  locale: string;
  locales: Locale[];
  translatedMenu?: MenuConfig;
}

export const translationStore = proxy<Translations>({
  translations: {},
  locale: getClientLocale(),
  locales: [],
});

export const translations = {
  async load(i18nextConfig?: InitOptions) {
    if (!i18nextConfig) {
      return;
    }

    // fetch list of locales from one-core
    const locales = await fetchLocales();
    await i18next.init({
      ...i18nextConfig,
      resources: {
        ...i18nextConfig.resources,
      },
    });
    // initialize the users' locale.
    const locale = detectLocale(locales);
    translations.changeLocale(locale?.code, true).catch(ignoreError);
    translationStore.locales = locales;
  },

  async changeLocale(locale: string, isInitialLoad: boolean = false) {
    // not the cleanest, but i18next recommends for checking which locales have been added already: https://github.com/i18next/i18next/issues/1068#issuecomment-400573808
    const loadedLanguages = Object.keys(
      i18next.services?.resourceStore?.data || {}
    );

    if (!loadedLanguages.includes(locale)) {
      const translation = await fetchLocale(locale);

      if (!translation) {
        return;
      }

      i18next.addResourceBundle(locale, 'translation', translation, true, true);
      i18next.loadLanguages([locale]).catch(ignoreError);
    }

    i18next.changeLanguage(locale).catch(ignoreError);

    translationStore.translations =
      i18next.services?.resourceStore?.data[locale].translation;
    translationStore.locale = locale;
    localStorage.setItem(StorageKeys.LOCALE, locale);

    if (!isInitialLoad) {
      localeService.setLocale(locale).catch(ignoreError);
    }

    // we don't need to load the menu for the initial language, because we have it already.
    // and for `cimode` we want to use translation keys, not real translations.
    if (isInitialLoad || locale === 'cimode') {
      translationStore.translatedMenu = undefined;
    } else {
      const translatedMenus = await configService.getMenuConfig();
      translationStore.translatedMenu = prepareMenu(translatedMenus.data);
    }
  },

  t(value?: string, bindingContext?: object) {
    return applyTranslationBinding(
      value,
      translationStore.translations,
      bindingContext
    );
  },
};

export const useTranslations = () =>
  useSnapshot(translationStore) as Translations;
